import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { CountryService } from 'src/app/entities/countries/state/country.service';
import { NotificationsDialogComponent } from 'src/app/entities/notifications/components/notifications-dialog/notifications-dialog.component';
import { NotificationsService } from 'src/app/entities/notifications/state/notifications.service';
import { RoutePaths } from 'src/app/routes/route-paths.enum';
import { IAreYouSureDialogData } from '@app-shared/dialogs/are-you-sure/are-you-sure-dialog.model';
import { AreYouSureComponent } from '@app-shared/dialogs/are-you-sure/are-you-sure.component';
import { SelectLanguageComponent } from '@app-shared/dialogs/select-language/select-language.component';
import { CountryCode } from '@app-shared/enums/country-codes.enum';
import { AsyncPipe } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { combineLatest, map, take } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { SettingsRepository } from '@app-core/state/settings.repository';
import { SessionRepository } from '@app-core/state/session.repository';
import { SessionService } from '@app-core/services/session.service';
import { NGQ_LANGUAGES_TOKEN } from '@app-core/injection-tokens/languages.token';
import { ITranslationLanguage } from '@app-core/interfaces/language.interface';
import { LanguageRepository } from '@app-core/state/language.repository';
import { TutorialService } from '@app-core/tutorial/tutorial.service';
import { NotificationsRepository } from 'src/app/entities/notifications/state/notification.repository';
import { CountryRepository } from 'src/app/entities/countries/state/country.repository';
import { filterNil } from '@ngneat/elf';

@Component({
  standalone: true,
  selector: 'sandoz-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    RouterModule,
    TranslocoModule,

    MatToolbarModule,
    MatBadgeModule,
    MatIconModule,
    MatButtonModule,
    MatRippleModule,
    MatDialogModule,
  ],
})
export class HeaderComponent implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly transloco = inject(TranslocoService);
  private readonly settingsRepo = inject(SettingsRepository);
  private readonly sessionService = inject(SessionService);
  private readonly tutorialService = inject(TutorialService);
  /* private readonly introService = inject(IntroService); */

  protected readonly languagesList =
    inject<ITranslationLanguage[]>(NGQ_LANGUAGES_TOKEN);
  protected readonly languageRepo = inject(LanguageRepository);
  protected readonly notificationService = inject(NotificationsService);
  protected readonly notificationRepo = inject(NotificationsRepository);
  protected readonly countryService = inject(CountryService);
  protected readonly countryRepo = inject(CountryRepository);

  protected readonly sessionRepo = inject(SessionRepository);

  readonly $isMobileMenuOpen = signal(false);

  readonly RoutePaths = RoutePaths;
  readonly CountryCode = CountryCode;

  readonly isSwitzerland$ = combineLatest([
    this.countryRepo.activeCountry$,
    this.sessionRepo.authUser$,
  ]).pipe(
    filterNil(),
    map(([activeCountry, authUser]) => {
      const isSwitzerland =
        activeCountry?.country_code === CountryCode.SWITZERLAND ||
        authUser?.region?.countries.some(
          country => country.country_code === CountryCode.SWITZERLAND,
        );

      return isSwitzerland;
    }),
  );

  ngAfterViewInit(): void {
    this.sessionRepo.isLoggedIn$.subscribe(loggedIn => {
      const user = this.sessionRepo.getStoreProp('authUser');

      if (loggedIn && user?.login_count === 1) {
        setTimeout(() => {
          this.doTutorial();
        }, 1500);
      }
    });
  }

  openLanguageSelectDialog(): void {
    this.dialog.open(SelectLanguageComponent, {
      restoreFocus: false,
      autoFocus: false,
      width: '500px',
    });
  }

  toggleTheme(): void {
    this.settingsRepo.setStoreProp(
      'theme',
      this.settingsRepo.getStoreProp('theme') === 'sandoz-light'
        ? 'sandoz-dark'
        : 'sandoz-light',
    );
  }

  closeMobileMenu(): void {
    this.$isMobileMenuOpen.set(false);
  }

  doTutorial(): void {
    this.tutorialService.startTutorial();
  }

  /* doIntro(): void {
    this.introService.startIntro();
  } */

  logout(): void {
    const dialogRef = this.dialog.open(AreYouSureComponent, {
      restoreFocus: false,
      autoFocus: false,
      minWidth: 250,
      width: '500px',
      data: {
        title: this.transloco.translate(
          'GENERAL.ARE_YOU_SURE_YOU_WANT_TO_LOGOUT',
        ),
      } as IAreYouSureDialogData,
    });

    dialogRef.afterClosed().subscribe(dialog_res => {
      if (dialog_res) {
        this.sessionService.logout();
        this.router.navigate([`/${RoutePaths.Login}`]);
      }
    });
  }

  openNotificationsDialog(): void {
    // Cancel any existing requests first
    this.notificationService.cancelRequest.next();

    const dialogRef = this.dialog.open(NotificationsDialogComponent, {
      restoreFocus: false,
      autoFocus: false,
      minWidth: 250,
      width: '90%',
      maxWidth: 900,
      data: {},
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        // Handle cleanup
        this.notificationService.destroyNotificationsSubscription();

        if (this.notificationRepo.getStoreProp('mode') === 'unread') {
          this.notificationService.removeReadNotificationsFromStore();
        }
      });
  }
}
